import React, { useState, useEffect, useCallback } from 'react'
import DropDown from '../../Input/DropDown'
import ErrorMessageWithExclamation from 'components/UI/ErrorMessageWithExclamation'
import axios from 'axios'
import { getFallbackCountries } from '../../../constants/fallbackCountries'
import getFlagCode from 'helpers/getFlagFallbacks'
const CountryDropdowns = ({ control, errors, Controller, isTutor }) => {
  const [countries, setCountries] = useState([])
  const [loading, setLoading] = useState(true)

  // Get countries both from the API and backend when possible
  useEffect(() => {
    const fetchCountriesFromBackend = async () => {
      try {
        // Try to get countries from the backend first (these will have proper IDs)
        const response = await axios.get('/api/v1/countries/')

        if (response.data && Array.isArray(response.data) && response.data.length > 0) {
          console.log(
            `Successfully fetched ${response.data.length} countries from backend`,
            response.data
          )

          // Format data from backend - this preserves the IDs needed for submission
          const formattedData = response.data
            .map((country) => ({
              key: country.id || country.pk, // Backend primary key
              value: country.id || country.pk, // Use ID as value for submission
              label: country.name,
              iso_code: country.iso_3166_1_a2 || country.code || country.iso_code, // Store ISO code
              searchLabel: `${country.name} (${
                country.iso_3166_1_a2 || country.code || country.iso_code
              })`,
            }))
            .sort((a, b) => a.label.localeCompare(b.label))

          setCountries(formattedData)
          setLoading(false)
          return true
        }
        return false
      } catch (error) {
        console.warn(
          'Could not fetch countries from backend, falling back to REST Countries API',
          error
        )
        return false
      }
    }

    // If backend fetch fails, use REST Countries API as fallback
    const fetchCountriesFromRESTAPI = async () => {
      try {
        console.log('Fetching countries from REST Countries API...')

        // Using the all endpoint with name and cca2 fields only
        const response = await fetch('https://restcountries.com/v3.1/all?fields=name,cca2')
        const data = await response.json()

        if (Array.isArray(data) && data.length > 0) {
          // Transform the data into the format needed for dropdowns
          // NOTE: These won't have proper backend IDs, so form submission may fail
          const formattedData = data
            .map((country) => ({
              key: country.cca2,
              value: country.cca2, // This is the ISO code, not the backend PK
              label: country.name.common,
              iso_code: country.cca2,
              searchLabel: `${country.name.common} (${country.cca2})`,
            }))
            .sort((a, b) => a.label.localeCompare(b.label))

          console.log(
            `Successfully loaded ${formattedData.length} countries from REST Countries API`
          )
          setCountries(formattedData)
        } else {
          console.error('REST Countries API returned invalid data', data)
          setCountries(getFallbackCountries())
        }
      } catch (error) {
        console.error('Error fetching countries from REST Countries API:', error)
        setCountries(getFallbackCountries())
      } finally {
        setLoading(false)
      }
    }

    const loadCountries = async () => {
      const backendSuccess = await fetchCountriesFromBackend()
      if (!backendSuccess) {
        await fetchCountriesFromRESTAPI()
      }
    }

    // const loadCountries = async () => {
    //   setCountries(getFallbackCountries())
    // }

    loadCountries()
  }, [])

  // Loading placeholder for dropdowns
  const loadingItems = [{ key: 'loading', value: '', label: 'Loading countries...' }]

  // Items to use in the dropdown
  const dropdownItems = loading ? loadingItems : countries

  const getFlags = (code) => {
    if (code === 'XK') {
      return 'https://flagcdn.com/16x12/xk.png' // Kosovo doesn't have an official ISO code, so we use Serbia's flag as a fallback
    }
    return `https://flagsapi.com/${getFlagCode(code)}/flat/24.png`
  }
  console.log(dropdownItems)

  return (
    <>
      <div className="flex flex-col items-start justify-start w-full !mb-4">
        <span className="inline-block text-sm font-medium mb-[6px]">Country of Origin</span>

        <Controller
          control={control}
          name="country_of_origin"
          render={({ field: { value, onChange } }) => (
            <DropDown
              className="!mb-[6px] text-primary-600"
              placeholder="Select Country of Origin"
              menuItems={dropdownItems}
              value={value}
              setValue={onChange}
              renderItem={(item) => (
                <div className="flex items-center gap-2">
                  <img src={getFlags(item?.iso_code)} className="w-5 h-5" alt="" />

                  <span className="text-primary-600">{item?.label}</span>
                </div>
              )}
            />
          )}
        />
        <span className="text-xs text-primary-75">
          {isTutor
            ? 'This field is visible to all students'
            : 'This field is visible to all tutors'}
        </span>
        {errors.country_of_origin && (
          <ErrorMessageWithExclamation text={errors.country_of_origin.message} />
        )}
      </div>

      <div className="flex flex-col items-start justify-start w-full !mb-4">
        <span className="inline-block text-sm font-medium mb-[6px]">Country of Residence</span>

        <Controller
          control={control}
          name="country_of_residence"
          render={({ field: { onChange, value } }) => (
            <DropDown
              className="!mb-[6px]"
              placeholder="Select Country of Residence"
              menuItems={dropdownItems}
              value={value}
              setValue={onChange}
              renderItem={(item) => (
                <div className="flex items-center gap-2">
                  <img src={getFlags(item?.iso_code)} className="w-5 h-5" alt="" />
                  <span className="text-primary-600">{item?.label}</span>
                </div>
              )}
            />
          )}
        />
        <span className="text-xs text-primary-75">
          {' '}
          {isTutor
            ? 'This field is visible to all students'
            : 'This field is visible to all tutors'}
        </span>
        {errors.country_of_residence && (
          <ErrorMessageWithExclamation text={errors.country_of_residence.message} />
        )}
      </div>
    </>
  )
}

export default CountryDropdowns
